import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import * as Highcharts from 'highcharts';
import { ModuleAgentChatChartBlock } from '@app/core/module-agent-chat.service';

@Component({
  standalone: false,
  selector: 'app-agent-chat-chart-block',
  templateUrl: './agent-chat-chart-block.component.html',
  styleUrls: ['./agent-chat-chart-block.component.scss']
})
export class AgentChatChartBlockComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() block: ModuleAgentChatChartBlock | null = null;
  @Output() chartRendered = new EventEmitter<void>();
  @ViewChild('chartHost') chartHost?: ElementRef<HTMLDivElement>;

  public hasSeries = false;

  private chart?: Highcharts.Chart;
  private viewReady = false;

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['block']) {
      this.renderChart();
    }
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private renderChart(): void {
    if (!this.viewReady) {
      return;
    }

    const host = this.chartHost?.nativeElement;
    this.destroyChart();
    this.hasSeries = false;

    if (!host || !this.block) {
      this.chartRendered.emit();
      return;
    }

    const chartSeries = this.buildSeries();
    const hasAnyPoints = chartSeries.some(series => {
      const seriesData = (series as any).data;
      return Array.isArray(seriesData) && seriesData.length > 0;
    });
    this.hasSeries = hasAnyPoints;

    if (!hasAnyPoints) {
      this.chartRendered.emit();
      return;
    }

    this.chart = Highcharts.chart(host, this.buildOptions(chartSeries));
    this.chartRendered.emit();
  }

  private buildSeries(): Highcharts.SeriesOptionsType[] {
    const inputSeries = this.block?.chartData?.series || [];
    const outputSeries: Highcharts.SeriesOptionsType[] = [];
    const seriesType = this.getSeriesType();

    for (const item of inputSeries) {
      const points = Array.isArray(item?.data)
        ? item.data
            .map(point => this.normalizePoint(point))
            .filter((point): point is [number, number | null] => !!point)
        : [];
      if (!points.length) {
        continue;
      }

      if (seriesType === 'column') {
        outputSeries.push({
          type: 'column',
          id: item.id,
          name: String(item?.name || ''),
          data: points,
          color: item.color,
          showInLegend: item.showInLegend,
          linkedTo: item.linkedTo || undefined,
          turboThreshold: 0
        } as Highcharts.SeriesColumnOptions);
      } else {
        const lineType = item.type === 'spline' ? 'spline' : 'line';
        outputSeries.push({
          type: lineType,
          id: item.id,
          name: String(item?.name || ''),
          data: points,
          color: item.color,
          zoneAxis: item.zoneAxis,
          zones: item.zones,
          showInLegend: item.showInLegend,
          linkedTo: item.linkedTo || undefined,
          marker: {
            enabled: false
          },
          turboThreshold: 0
        } as Highcharts.SeriesLineOptions | Highcharts.SeriesSplineOptions);
      }
    }

    return outputSeries;
  }

  private normalizePoint(point: any): [number, number | null] | null {
    if (!Array.isArray(point) || point.length < 2) {
      return null;
    }

    const x = Number(point[0]);
    if (!Number.isFinite(x)) {
      return null;
    }

    const rawY = point[1];
    if (rawY === null || rawY === undefined || rawY === '') {
      return [x, null];
    }

    const y = Number(rawY);
    if (!Number.isFinite(y)) {
      return null;
    }

    return [x, y];
  }

  private buildOptions(
    series: Highcharts.SeriesOptionsType[]
  ): Highcharts.Options {
    const dark = this.isDarkTheme();
    const axisColor = dark ? '#64748b' : '#94a3b8';
    const gridColor = dark ? '#334155' : '#e2e8f0';
    const labelColor = dark ? '#cbd5e1' : '#334155';
    const seriesType = this.getSeriesType();

    return {
      chart: {
        type: seriesType,
        backgroundColor: 'transparent',
        height: 280,
        spacing: [8, 8, 8, 8]
      },
      title: {
        text: undefined
      },
      credits: {
        enabled: false
      },
      legend: {
        enabled: series.length > 1,
        itemStyle: {
          color: labelColor,
          fontSize: '11px'
        }
      },
      xAxis: {
        type: 'datetime',
        lineColor: axisColor,
        tickColor: axisColor,
        labels: {
          style: {
            color: labelColor
          }
        }
      },
      yAxis: {
        title: {
          text: null
        },
        lineColor: axisColor,
        tickColor: axisColor,
        gridLineColor: gridColor,
        labels: {
          style: {
            color: labelColor
          }
        }
      },
      tooltip: {
        shared: true,
        xDateFormat: '%d/%m/%Y %H:%M'
      },
      plotOptions: {
        series: {
          animation: false
        },
        column: {
          pointPadding: 0.05,
          borderWidth: 0,
          groupPadding: 0.08
        }
      },
      series
    };
  }

  private getSeriesType(): 'line' | 'column' {
    const chartKind = String(this.block?.chart?.chartKind || '')
      .trim()
      .toLowerCase();
    return chartKind === 'flow' ? 'column' : 'line';
  }

  private isDarkTheme(): boolean {
    return !!document?.body?.classList?.contains('dark-theme');
  }

  private destroyChart(): void {
    if (!this.chart) {
      return;
    }
    try {
      this.chart.destroy();
    } catch (_) {
      // Avoid hard break if Highcharts has already disposed resources.
    } finally {
      this.chart = undefined;
    }
  }
}
